import { useQuery } from '@tanstack/react-query'

import { dataOrThrow } from '@snootsvet/shared/portal'

import { usePublicBookingApi } from '@/customer/hooks/use-api'

/**
 * React Query key prefix for public available-slots reads. Exported
 * so the booking mutation hook can invalidate every slot range at
 * once without having to know the specific token / date-range tuple
 * that's currently mounted.
 */
export const publicAvailableSlotsQueryKey = ['public-available-slots'] as const

interface UsePublicAvailableSlotsInput {
  /** Inclusive start of the range, as a YYYY-MM-DD clinic-local date. */
  from: string
  /** Inclusive end of the range, as a YYYY-MM-DD clinic-local date. */
  to: string
}

/**
 * Loads bookable slots for the signed-link booking flow via
 * `GET /api/public/available-slots`. Authenticated by the token
 * from the URL, forwarded via `usePublicBookingApi`.
 *
 * Accepts a nullable token and input so callers can mount the hook
 * before URL params are validated; the query stays disabled until
 * both are populated.
 */
export function usePublicAvailableSlots(
  token: string | null,
  input: UsePublicAvailableSlotsInput | null,
  options: { enabled?: boolean } = {}
) {
  const api = usePublicBookingApi(token)

  return useQuery({
    queryKey: [...publicAvailableSlotsQueryKey, token, input?.from, input?.to] as const,
    enabled: (options.enabled ?? true) && !!token && input !== null,
    queryFn: async () => {
      if (!input) throw new Error('usePublicAvailableSlots: input is null')
      return dataOrThrow(await api.get('/public/available-slots', { query: input }))
    },
  })
}
